/**
 * Director-context HealthBar — RFC-005 peak-period 104-pt scoring display.
 *
 * Renders a horizontal bar filled to `score / 100` with colour-coded
 * segments (HIGH=red, MEDIUM=amber, LOW=green). An optional modifier dot
 * pins the score modifier callout (HIGH=-15, MEDIUM=-8) at the right edge.
 *
 * Props kept minimal: `score` (0-100), `band` (HIGH/MEDIUM/LOW),
 * `showModifier` (default false). No query logic — pure presentational.
 */
import type { CSSProperties } from 'react'

export type RiskBand = 'HIGH' | 'MEDIUM' | 'LOW'

export interface HealthBarProps {
  /** 0–100 score from the 104-pt peak-period scoring model. */
  score: number
  /** Risk band mapped from the score. */
  band: RiskBand
  /** When true, shows the modifier dot (HIGH=-15, MEDIUM=-8) at the end. */
  showModifier?: boolean
  /** Optional test id override. Defaults to `director-health-bar`. */
  testId?: string
}

// Policy §5: band thresholds and modifier values.
const BAND_MODIFIER: Record<Exclude<RiskBand, 'LOW'>, number> = {
  HIGH: -15,
  MEDIUM: -8,
}

const BAND_COLOURS: Record<RiskBand, string> = {
  HIGH: 'var(--danger)',
  MEDIUM: 'var(--warning)',
  LOW: 'var(--success)',
}

const BAND_BACKGROUND: Record<RiskBand, string> = {
  HIGH: 'var(--danger-bg)',
  MEDIUM: 'var(--warning-bg)',
  LOW: 'var(--surface-2)',
}

/**
 * Reusable health bar for Director risk-band surfaces.
 *
 * Scores are clamped to [0, 100]. The bar fill colour maps directly to
 * the band — not interpolated — so any score in the HIGH bucket shows
 * the same red regardless of whether it's 80 or 100.
 */
export function HealthBar({
  score,
  band,
  showModifier = false,
  testId = 'director-health-bar',
}: HealthBarProps): JSX.Element {
  const clamped = Math.min(100, Math.max(0, score))
  const fillPct = clamped
  const fill = BAND_COLOURS[band]
  const modifier = band !== 'LOW' ? BAND_MODIFIER[band] : null

  return (
    <div
      data-testid={testId}
      data-band={band}
      style={containerStyle}
      role="meter"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Risk score ${clamped} — ${band}`}
    >
      {/* Track */}
      <div style={trackStyle}>
        <div
          data-testid={`${testId}-fill`}
          style={{
            ...fillBaseStyle,
            width: `${fillPct}%`,
            background: fill,
          }}
        />
      </div>

      {/* Score label */}
      <span
        data-testid={`${testId}-score`}
        style={{
          ...scoreLabelStyle,
          color: fill,
        }}
      >
        {clamped}
      </span>

      {/* Modifier dot — only for MEDIUM/HIGH when showModifier is true */}
      {showModifier && modifier !== null ? (
        <span
          data-testid={`${testId}-modifier`}
          style={{
            ...modifierStyle,
            background: BAND_BACKGROUND[band],
            borderColor: fill,
            color: fill,
          }}
        >
          {modifier}
        </span>
      ) : null}
    </div>
  )
}

// ── styles ────────────────────────────────────────────────────────────────────

const containerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
}

const trackStyle: CSSProperties = {
  flex: 1,
  height: 8,
  borderRadius: 999,
  background: 'var(--surface-2)',
  border: '1px solid var(--line-subtle)',
  overflow: 'hidden',
}

const fillBaseStyle: CSSProperties = {
  height: '100%',
  borderRadius: 999,
  transition: 'width 300ms ease-out',
}

const scoreLabelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  fontFamily: 'var(--font-mono)',
  minWidth: 24,
  textAlign: 'right',
  lineHeight: 1,
}

const modifierStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  fontFamily: 'var(--font-mono)',
  padding: '2px 6px',
  borderRadius: 999,
  border: '1px solid',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
}
